import type { BaseSettings } from './TimeAwareBase';
import { TimeInvariantBase } from './TimeInvariantBase';
import {
    AlphaAttribute,
    ColorAttribute,
    NumberAttribute,
    getAttributesToSerialize,
} from './attributes';

type GradientPointSettings = Partial<BaseSettings> & {
    x?: number;
    y?: number;
    color?: number;
    alpha?: number;
};

/**
 * An endpoint of a linear gradient: its position in normalized shape coordinates plus the colour
 * and opacity of the gradient stop at that point. (0, 0) is the top-left of the shape's bounding
 * box and (1, 1) the bottom-right; values may run slightly outside that range so the transition can
 * start/end off the shape (a partial gradient). Being a child object connected to the update flow
 * (like Position), mutating x/y/color/alpha notifies the parent graphic to redraw.
 */
export class GradientPoint extends TimeInvariantBase {
    override _classType = 'GradientPoint';
    override _name = 'GradientPoint';

    @NumberAttribute({ min: -1, max: 2, animatable: false })
    accessor x = 0;
    @NumberAttribute({ min: -1, max: 2, animatable: false })
    accessor y = 0.5;
    @ColorAttribute()
    accessor color = 0xff0000;
    @AlphaAttribute()
    accessor alpha = 1;

    constructor(settings: GradientPointSettings) {
        super(settings as BaseSettings);
        // Apply the initial values without notifying the parent: a graphic constructs its points in
        // a field initializer, so the parent is still mid-construction and must not be updated yet.
        // autoUpdate is restored to true so that subsequent mutations do drive a redraw.
        this.autoUpdate = false;
        if (settings.x !== undefined) this.x = settings.x;
        if (settings.y !== undefined) this.y = settings.y;
        if (settings.color !== undefined) this.color = settings.color;
        if (settings.alpha !== undefined) this.alpha = settings.alpha;
        this.requiresUpdate = false;
        this.autoUpdate = true;
    }

    override get readonlyProperties(): string[] {
        return [];
    }

    override get propertiesToSerialize() {
        return getAttributesToSerialize(this);
    }

    override get editableProperties() {
        return getAttributesToSerialize(this);
    }
}
